import "./DashboardPage.styles.scss";
import React, { useState, useMemo, useEffect } from "react";
import {
  PageActionBar,
  StatsCard,
  SidePanel,
  TopNav,
  PageTitle,
} from "components";
import {
  useGetPatients,
  useFetchSubmissions,
  useCurrentUser,
  useFetchPatients,
} from "utils/hooks";
import {
  useOrganizationSettings,
  getPatientWordCapitalized,
  getPatientWordPlural,
} from "utils/hooks/organizations/useOrganizationSettings";
import { useSelector } from "react-redux";
import DashboardEmptyState from "./components/DashboardEmptyState";
import RecentSubmissionsCard from "./components/RecentSubmissionsCard";
import SubmissionsIcon from "assets/icons/inbox.svg";
import PatientsIcon from "assets/icons/users-medical.svg";
import AddPatientButton from "../Patients/components/AddPatientButton";
import AddPatientsForm from "../Patients/components/AddPatientsForm";
import { useTranslation } from "react-i18next";

const DashboardPage = () => {
  const [showAddPatient, setShowAddPatient] = useState(false);
  const { patients } = useGetPatients({});
  const { fetchAllSubmissions } = useFetchSubmissions();
  const { fetchAllPatients } = useFetchPatients();
  const { settings: orgSettings } = useOrganizationSettings();
  const submissions = useSelector(
    (state: any) => state.submissions?.submissions || []
  );
  const memoizedPatients = useMemo(() => patients, [patients]);
  const memoizedSubmissions = useMemo(() => submissions, [submissions]);
  const { user } = useCurrentUser();
  const isEmpty = !patients.length;
  const { t } = useTranslation(["common"]);
  const title = t("page_titles.dashboard_title");

  // Fetch dashboard data when component mounts or when organization changes
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch both submissions and patients data
        await Promise.all([fetchAllSubmissions(), fetchAllPatients()]);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };

    fetchDashboardData();
  }, [fetchAllSubmissions, fetchAllPatients, user?.activeOrganizationId]);

  return (
    <>
      <TopNav topL={<PageTitle text={title} />} />
      <div className="dashboard-page">
        {!isEmpty && (
          <>
            <PageActionBar>
              <p className="dashboard-page__greeting">
                {t("dashboard.calm_greeting", { userName: user?.firstName })} 👋
              </p>
              <div className="flex">
                <div style={{ marginInlineStart: "1em" }}>
                  <AddPatientButton
                    onClick={() => {
                      setShowAddPatient(!showAddPatient);
                    }}
                  />
                </div>
              </div>
            </PageActionBar>
          </>
        )}
        {!isEmpty ? (
          <div className="dashboard-page__wrapper flex justify-between">
            <div className="dashboard-page__wrapper--widget-primary">
              <RecentSubmissionsCard
                data={memoizedSubmissions}
                color="primary"
              />
            </div>
            <div className="dashboard-page__wrapper--stats--column">
              <div className="dashboard-page__wrapper--stats-title">Stats</div>
              <div className="dashboard-page__wrapper--stats">
                <StatsCard
                  data={memoizedSubmissions}
                  title="Submissions"
                  color="primary"
                  icon={SubmissionsIcon}
                />
              </div>
              <div className="dashboard-page__wrapper--stats">
                <StatsCard
                  data={memoizedPatients}
                  title={t("patients.patient_label", {
                    patientWord: getPatientWordPlural(orgSettings),
                  })}
                  color="secondary"
                  icon={PatientsIcon}
                />
              </div>
            </div>
          </div>
        ) : (
          <DashboardEmptyState user={user} orgSettings={orgSettings} />
        )}
      </div>
      <SidePanel
        title={t("patients.add_patient_button", {
          patientWordCapitalized: getPatientWordCapitalized(orgSettings),
        })}
        isVisible={showAddPatient}
        onClose={() => setShowAddPatient(false)}
      >
        <AddPatientsForm onSuccess={() => setShowAddPatient(false)} />
      </SidePanel>
    </>
  );
};

export default DashboardPage;
